import React, { useEffect, useState } from "react";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import QuickActionsSidebar from "../DistributorDashboard/QuickActionsSidebar";
import { getActionsByUserType } from "../../../config/actions";
import Pagination from "../../common/Pagination";
import { helpRequest } from "./helpApi";
import "../DistributorsAdd/DistributorsAdd.css";
import "../DistributorDashboard/DistributorDashboard.css";
import "./HelpContent.css";

const PAGE_SIZE = 5;

const htmlToEditorState = (html) => {
  if (!html) return EditorState.createEmpty();
  const { contentBlocks, entityMap } = htmlToDraft(html);
  return EditorState.createWithContent(
    ContentState.createFromBlockArray(contentBlocks, entityMap)
  );
};

const HelpFaqs = () => {
  const userType = localStorage.getItem("user_type");
  const userId = localStorage.getItem("user_id");
  const actions = getActionsByUserType(userType, userId);
  const [expandedMenu, setExpandedMenu] = useState(null);

  const isAdmin = userType === "7";

  const [distributorFilter, setDistributorFilter] = useState("");
  const [distributorsList, setDistributorsList] = useState([]);

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [question, setQuestion] = useState("");
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const data = await helpRequest("/help/faqs");
      setFaqs(data.faqs || []);
    } catch (err) {
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
    if (isAdmin) {
      helpRequest("/admin/distributors")
        .then((data) => setDistributorsList(data.distributors || []))
        .catch(() => setDistributorsList([]));
    }
  }, []);

  const filteredFaqs = distributorFilter
    ? faqs.filter((f) => f.distributor_id === distributorFilter)
    : faqs;

  const totalPages = Math.max(1, Math.ceil(filteredFaqs.length / PAGE_SIZE));
  const paginatedFaqs = filteredFaqs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const openForm = (faq = null) => {
    setEditing(faq);
    setQuestion(faq ? faq.question : "");
    setEditorState(htmlToEditorState(faq ? faq.answer : ""));
    setError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!question.trim() || !editorState.getCurrentContent().hasText()) {
      setError("Question and answer are required.");
      return;
    }
    const answer = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    setSaving(true);
    setError("");
    try {
      await helpRequest(editing ? `/help/faqs/${editing.id}` : "/help/faqs", {
        method: editing ? "PUT" : "POST",
        body: { question, answer },
      });
      setFormOpen(false);
      setEditing(null);
      await fetchFaqs();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await helpRequest(`/help/faqs/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      const remaining = filteredFaqs.length - 1;
      setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(remaining / PAGE_SIZE))));
      await fetchFaqs();
    } catch (err) {
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="distributors-add-container">
      <div className="distributors-add-dashboard">
        <QuickActionsSidebar
          actions={actions}
          expandedMenu={expandedMenu}
          setExpandedMenu={setExpandedMenu}
        />
        <div className="distributors-add-content">
          <div className="add-btn-wrp add-btn-wrp2">
            <div className="head-listing">
              <h3>FAQs</h3>
              <p className="head-listing-subtitle">
                Manage the frequently asked questions displayed in the Common Questions section of the customer support website.
              </p>
            </div>
            <div className="add-btn">
              <button onClick={() => openForm()}>Add FAQ</button>
            </div>
          </div>

          {isAdmin && (
            <div className="filters-header" style={{ margin: 0 }}>
              <div className="filter-group">
                <label style={{ margin: 0 }}>Filter Distributor:</label>
                <select
                  value={distributorFilter}
                  onChange={(e) => {
                    setDistributorFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Distributors</option>
                  {distributorsList.map((dist) => (
                    <option key={dist.id} value={dist.distributor_id}>
                      {`${dist.firstname || ""} ${dist.lastname || ""}`.trim() ||
                        dist.business ||
                        `Distributor #${dist.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="distributors-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="loader-spinner"></div>
                <p>Loading FAQs...</p>
              </div>
            ) : filteredFaqs.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-question-circle"></i>
                <p>{faqs.length === 0 ? "No FAQs yet" : "No FAQs found"}</p>
              </div>
            ) : (
              <>
                <div className="table-wrapper">
                  <table className="distributors-table help-table">
                    <thead>
                      <tr>
                        <th>Question</th>
                        <th>Answer</th>
                        <th className="help-col-actions">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedFaqs.map((faq) => (
                        <tr key={faq.id}>
                          <td>{faq.question}</td>
                          <td>
                            <div
                              className="help-faq-answer"
                              dangerouslySetInnerHTML={{ __html: faq.answer || "" }}
                            />
                          </td>
                          <td>
                            {faq.editable ? (
                              <div className="help-actions-cell">
                                <button
                                  className="action-btn"
                                  title="Edit"
                                  onClick={() => openForm(faq)}
                                >
                                  <i className="fa fa-pencil-square-o"></i>
                                </button>
                                <button
                                  className="action-btn cancel-btn"
                                  title="Delete"
                                  onClick={() => setDeleteTarget(faq)}
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </div>
                            ) : (
                              <div className="help-actions-cell help-readonly">
                                View only
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={formOpen} toggle={closeForm} centered size="lg">
        <ModalHeader className="modal-header-custom" toggle={closeForm}>
          {editing ? "Edit FAQ" : "Add FAQ"}
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          <div className="help-form-group">
            <label>Question*</label>
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </div>
          <div className="help-form-group">
            <label>Answer*</label>
            <div
              className="help-faq-editor"
              style={{ border: "1px solid rgb(77, 0, 77)", borderRadius: "4px" }}
            >
              <Editor
                editorState={editorState}
                onEditorStateChange={setEditorState}
                wrapperClassName="help-faq-editor-wrapper"
                editorClassName="help-faq-editor-body"
                toolbarClassName="help-faq-editor-toolbar"
              />
            </div>
          </div>
          {error && <div className="help-error">{error}</div>}
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button className="btn-secondary" onClick={closeForm} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary-modern"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered>
        <ModalHeader className="modal-header-custom" toggle={() => setDeleteTarget(null)}>Delete FAQ</ModalHeader>
        <ModalBody className="modal-body-custom">
          Delete "{deleteTarget?.question}"? This cannot be undone.
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button
            className="btn-secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="btn-primary-modern"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default HelpFaqs;
